import { ApplicationConfig, importProvidersFrom, inject, provideAppInitializer, provideBrowserGlobalErrorListeners, provideZoneChangeDetection } from '@angular/core';
import { provideRouter, withInMemoryScrolling } from '@angular/router';
import { providePrimeNG } from 'primeng/config';
import { NgxEchartsModule } from 'ngx-echarts';
import { provideHttpClient, withInterceptors } from '@angular/common/http';
import Aura from '@primeuix/themes/aura';
import { definePreset } from '@primeuix/themes';

const GalaxyPreset = definePreset(Aura, {
  semantic: {
    galaxy: {
      /* Dark background for the top navigation bar and desktop sidebar */
      surfaceHeader: '#2d2d2d',
      /* Severity: positive outcome, success state, valid input */
      severitySuccess: '#22c55e',
      /* Severity: caution or degraded state requiring attention */
      severityWarning: '#f59e0b',
      /* Severity: error, failure, or critical alert */
      severityDanger: '#ef4444',
      /* Fleet — engine actively running and vehicle in motion */
      engineOn: '#43e97b',
      /* Fleet — engine running but vehicle stationary (idle) */
      engineIdle: '#f0c674',
      /* Fleet — engine off */
      engineOff: '#667eea',
      /* Fleet — engine state cannot be determined */
      engineUnknown: '#6c757d',
      /* Fleet popup badge — asset currently active / moving */
      statusActive: '#2db862',
      /* Fleet popup badge — asset in idle state */
      statusIdle: '#c054d4',
      /* Fleet popup badge — asset stopped with engine off */
      statusStopped: '#6c757d',
      /* Fleet popup badge — alert or maintenance condition */
      statusAlert: '#e44d7b',
      /* Fleet popup badge — asset connected / online */
      statusConnected: '#667eea',
      /* Accent blue — used for general UI accents and icon chips */
      accentBlue: '#3b82f6',
      /* Accent purple — used for project/category icon chips and admin features */
      accentPurple: '#8b5cf6',
      /* Accent green — used for date/time icon chips and positive states */
      accentGreen: '#22c55e',
      /* Accent orange — used for reporter/user icon chips */
      accentOrange: '#f97316',
      /* Accent teal — used for assignee icon chips */
      accentTeal: '#14b8a6',
      /* Accent amber — used for "last updated" icon chips */
      accentAmber: '#f59e0b',
      /* Accent sky — used for description icon chips */
      accentSky: '#0ea5e9',
      /* Accent indigo — used for comments section icon chips */
      accentIndigo: '#6366f1',
      /* Accent fuchsia — used for harsh turning safety event icon */
      accentFuchsia: '#d946ef',
      /* Chart series 1 — primary data series (requests, active jobs) */
      chartSeries1: '#3b82f6',
      /* Chart series 2 — secondary data series (errors, paused) */
      chartSeries2: '#ef4444',
      /* Chart series 3 — tertiary data series (avg duration, warning) */
      chartSeries3: '#f59e0b',
      /* Chart series 4 — quaternary data series (user activity, proppant) */
      chartSeries4: '#8b5cf6',
      /* Chart series 5 — quinary data series (controller breakdown, fluid) */
      chartSeries5: '#06b6d4',
      /* Chart series 6 — additional data series (completed stages) */
      chartSeries6: '#22c55e',
      /* Chart gradient — start color for bar/area gradient fills */
      chartGradientStart: '#667eea',
      /* Chart gradient — end color for bar/area gradient fills */
      chartGradientEnd: '#764ba2',
      /* Chart axis and gridline label color */
      chartAxisColor: '#9ca3af',
      /* Chart grid line and border color */
      chartGridColor: '#374151',
      /* Chart emphasis/tooltip highlight text color */
      chartEmphasisText: '#e5e7eb',
      /* Avatar in the top navigation bar */
      avatarHeader: '#667eea',
      /* Rotating comment avatar palette — index 0 (indigo) */
      avatarPalette0: '#6366f1',
      /* Rotating comment avatar palette — index 1 (cyan) */
      avatarPalette1: '#06b6d4',
      /* Rotating comment avatar palette — index 2 (amber) */
      avatarPalette2: '#f59e0b',
      /* Rotating comment avatar palette — index 3 (emerald) */
      avatarPalette3: '#10b981',
      /* Rotating comment avatar palette — index 4 (rose) */
      avatarPalette4: '#f43f5e',
      /* Driver avatar — gradient start color for the driver icon circle */
      gradientDriverStart: '#667eea',
      /* Driver avatar — gradient end color for the driver icon circle */
      gradientDriverEnd: '#764ba2',
      /* Support / AI chat panel — primary surface background start */
      supportAiSurfaceFrom: '#172554',
      /* Support / AI chat panel — primary surface background end */
      supportAiSurfaceTo: '#000000',
      /* Support / AI chat panel — border and divider color */
      supportAiBorder: '#1e3a8a',
      /* Support / AI chat panel — accent color for icons and active highlights */
      supportAiAccent: '#60a5fa',
      /* Support / AI chat panel — primary text color */
      supportAiText: '#eff6ff',
      /* Support / AI chat panel — secondary / muted text color */
      supportAiTextMuted: '#93c5fd',
      /* Support / AI chat panel — active conversation item background */
      supportAiConvoActive: '#1e3a8a',
      /* Support / AI chat panel — user message bubble background */
      supportAiMessageUser: '#1d4ed8',
      /* Support / AI chat panel — AI message bubble background */
      supportAiMessageAi: '#172554',
      /* Support inbox — unread message indicator dot */
      supportUnreadDot: '#f97316',
      /* KPI card blue — background gradient start */
      kpiCardBlueFrom: '#1e3a8a',
      /* KPI card blue — background gradient end */
      kpiCardBlueTo: '#172554',
      /* KPI card blue — label and subtitle text */
      kpiCardBlueText: '#bfdbfe',
      /* KPI card blue — icon color */
      kpiCardBlueIcon: '#60a5fa',
      /* KPI card green — background gradient start */
      kpiCardGreenFrom: '#14532d',
      /* KPI card green — background gradient end */
      kpiCardGreenTo: '#052e16',
      /* KPI card green — label and subtitle text */
      kpiCardGreenText: '#bbf7d0',
      /* KPI card green — icon color */
      kpiCardGreenIcon: '#4ade80',
      /* KPI card purple — background gradient start */
      kpiCardPurpleFrom: '#4c1d95',
      /* KPI card purple — background gradient end */
      kpiCardPurpleTo: '#2e1065',
      /* KPI card purple — label and subtitle text */
      kpiCardPurpleText: '#e9d5ff',
      /* KPI card purple — icon color */
      kpiCardPurpleIcon: '#c084fc',
      /* KPI card orange — background gradient start */
      kpiCardOrangeFrom: '#7c2d12',
      /* KPI card orange — background gradient end */
      kpiCardOrangeTo: '#431407',
      /* KPI card orange — label and subtitle text */
      kpiCardOrangeText: '#fed7aa',
      /* KPI card orange — icon color */
      kpiCardOrangeIcon: '#fb923c',
    }
  }
});
import {
  MsalService,
  MsalGuard,
  MsalInterceptor,
  MSAL_INSTANCE,
  MSAL_GUARD_CONFIG,
  MSAL_INTERCEPTOR_CONFIG,
  MsalInterceptorConfiguration,
  MsalGuardConfiguration,
  MsalBroadcastService
} from '@azure/msal-angular';
import {
  IPublicClientApplication,
  PublicClientApplication,
  InteractionType,
  BrowserCacheLocation,
  LogLevel
} from '@azure/msal-browser';

import { provideMarkdown } from 'ngx-markdown';
import { routes } from './app.routes';
import { msalTokenInterceptor } from './core/interceptors/msal-token.interceptor';
import { versionInterceptor } from './core/interceptors/version.interceptor';
import { apiErrorInterceptor } from './core/interceptors/api-error.interceptor';
import { environment } from '../environments/environment';
import { API_BASE_URL } from './core/api/galaxy-api-client';
import { SCARLETT_API_BASE_URL } from './core/api/scarlett-api-client';
import { MessageService } from 'primeng/api';
import { GRAFANA_ON_PREM_EMBED_CONFIG } from './features/analytics/models/grafana-on-prem-embed.models';
import { GRAFANACLOUD_BASE_URL, GRAFANACLOUD_EMBED_CONFIG } from './features/analytics/grafana-cloud-dashboard/models/grafana-cloud-embed.models';

export function MSALInstanceFactory(): IPublicClientApplication {
  return new PublicClientApplication({
    auth: {
      clientId: environment.msal.clientId,
      authority: environment.msal.authority,
      redirectUri: environment.msal.redirectUri + '/auth/login',
      postLogoutRedirectUri: environment.msal.redirectUri + '/auth/login',
      // We navigate to the post-login destination ourselves (LoginComponent reads
      // MSAL's returned `state` and calls router.navigateByUrl). Leaving this on
      // its default (true) makes MSAL ALSO perform its own window.location redirect
      // back to the pre-login URL, causing a second full page reload — and a second
      // fresh LoginComponent mount — on top of the one unavoidable AAD round trip.
      navigateToLoginRequestUrl: false,
    },
    cache: {
      cacheLocation: BrowserCacheLocation.LocalStorage,
      storeAuthStateInCookie: true
    },
    system: {
      loggerOptions: {
        loggerCallback: (level: LogLevel, message: string) => {
          switch (level) {
            case LogLevel.Error:
              console.error(message);
              break;
            case LogLevel.Warning:
              console.warn(message);
              break;
            default:
              break;
          }
        },
        logLevel: LogLevel.Warning
      }
    }
  });
}

export function MSALInterceptorConfigFactory(): MsalInterceptorConfiguration {
  const protectedResourceMap = new Map<string, Array<string>>();

  // Add your API endpoints that require authentication
  // protectedResourceMap.set('https://your-api.com/api/*', ['api://your-api-client-id/access_as_user']);

  return {
    interactionType: InteractionType.Redirect,
    protectedResourceMap
  };
}

export function MSALGuardConfigFactory(): MsalGuardConfiguration {
  return {
    interactionType: InteractionType.Redirect,
    authRequest: {
      scopes: environment.msal.apiScopes
    }
  };
}

export const appConfig: ApplicationConfig = {
  providers: [
    // provideZoneChangeDetection({ eventCoalescing: true }),
    provideBrowserGlobalErrorListeners(),
    provideRouter(
      routes,
      withInMemoryScrolling({
        scrollPositionRestoration: 'top',
        anchorScrolling: 'enabled'
      })
    ),
    provideHttpClient(
      withInterceptors([msalTokenInterceptor, versionInterceptor, apiErrorInterceptor])
    ),
    providePrimeNG({
      theme: {
        preset: GalaxyPreset,
        options: {
          darkModeSelector: '.my-app-dark'
        }
      }
    }),
    importProvidersFrom(
      NgxEchartsModule.forRoot({
        echarts: () => import('echarts')
      })
    ),
    {
      provide: API_BASE_URL,
      useValue: environment.baseApiUrl.replace(/\/+$/, '') // Remove trailing slashes
    },
    {
      // Scarlett is reached SAME-ORIGIN in every environment, so her base URL is empty — the
      // generated client's paths (`/api/v1/scarlett/...`) resolve against the current origin.
      //
      // Deliberately a constant rather than a per-environment `environment.*` value, because it is
      // genuinely the same everywhere and a field duplicated across five files invites one of them
      // drifting into an absolute URL:
      //   - deployed: nginx in this app proxies /api/v1/scarlett/ to Scarlett's INTERNAL ingress
      //     (she has no public ingress, and deliberately no CORS policy, by design).
      //   - local: the dev-server proxy (proxy.conf.json, wired via angular.json's serve target)
      //     forwards the same prefix to https://localhost:7210.
      //
      // The upshot is that the browser never issues a cross-origin request to Scarlett in ANY
      // environment, local included — so local development needs no CORS exception on her side, and
      // there is no local-only bypass that a deployed instance could exploit.
      provide: SCARLETT_API_BASE_URL,
      useValue: ''
    },
    {
      provide: GRAFANA_ON_PREM_EMBED_CONFIG,
      useValue: environment.grafanaOnPrem
    },
    {
      provide: GRAFANACLOUD_BASE_URL,
      useValue: environment.grafanaCloud.baseUrl.replace(/\/+$/, '') // Remove trailing slashes
    },
    {
      provide: GRAFANACLOUD_EMBED_CONFIG,
      useValue: environment.grafanaCloud
    },
    {
      provide: MSAL_INSTANCE,
      useFactory: MSALInstanceFactory
    },
    {
      provide: MSAL_GUARD_CONFIG,
      useFactory: MSALGuardConfigFactory
    },
    {
      provide: MSAL_INTERCEPTOR_CONFIG,
      useFactory: MSALInterceptorConfigFactory
    },
    MessageService,
    MsalService,
    MsalGuard,
    MsalBroadcastService,
    provideAppInitializer(() => inject(MsalService).instance.initialize()),
    provideMarkdown()
  ]
};
